/**
 * What the room list says about each room, worked out from the list endpoint.
 *
 * "What have they found" is the interesting half, and it is the product's actual
 * claim. Rooms opened is a vanity number; companies mapped and complaints
 * corroborated is the thing somebody is paying for — and it was invisible
 * without opening every room one at a time.
 *
 * Everything comes from `BoardroomSummary`, which the list endpoint already
 * returns per room. Pure and dependency-free so CRA's jest can test it.
 */
import type { BoardroomSummary } from '../types';

/** The kinds worth counting on a home screen, in the order a reader wants them. */
export const TALLY_KINDS: [string, string][] = [
  ['IDEA', 'ideas'],
  ['COMPANY', 'companies'],
  ['PRODUCT', 'products'],
  ['PAIN', 'pains'],
  ['EVIDENCE', 'evidence'],
];

/** One room's board in a line: "4 ideas · 12 companies". */
export function tally(counts: Record<string, number> | undefined): string {
  const parts = TALLY_KINDS.filter(([k]) => counts?.[k]).map(
    ([k, label]) => `${counts![k]} ${label}`,
  );
  return parts.length ? parts.join(' · ') : 'nothing yet';
}

/** How many results are sitting unread, and how many frame changes wait on you. */
export function waitingOn(room: BoardroomSummary): { results: number; proposals: number } {
  return { results: room.waiting?.results ?? 0, proposals: room.waiting?.proposals ?? 0 };
}
